/**
 * Unit display + axis-grouping helpers for the series chart.
 *
 * A multi-series chart can mix volume (SCF/MSCF) and energy (MMBtu) sources;
 * those belong on separate y-axes or the smaller magnitude flattens to the
 * baseline. `unitGroup` decides which axis a series lands on.
 */

const UNIT_LABEL: Record<string, string> = {
  scf: "SCF",
  mscf: "MSCF",
  mmbtu: "MMBtu",
  dth: "Dth",
  kwh: "kWh",
  lbs: "lbs",
  HHV: "MMBtu HHV",
  LHV: "MMBtu LHV",
};

export function unitLabel(unit: string): string {
  return UNIT_LABEL[unit] ?? unit;
}

// "volume" / "energy" for unit families we can normalize within (see
// plotScale); any other unit is its own group keyed by the exact unit string,
// so unrelated units (kWh vs lbs) land on separate axes instead of being
// collapsed together and plotted on one mis-scaled axis.
export function unitGroup(unit: string): string {
  if (unit === "scf" || unit === "mscf") return "volume";
  if (unit === "mmbtu" || unit === "dth" || unit === "HHV" || unit === "LHV") return "energy";
  return unit;
}

/**
 * Magnitude factor to convert a unit into the base unit of its group, so units
 * that share a y-axis but differ only by scale (SCF vs MSCF — a 1000× gap) plot
 * on a common scale instead of the smaller one flattening to the baseline.
 * Used ONLY for chart geometry; the exact decimal string still drives the
 * tooltip/CSV, so this never recomputes a compliance figure. Unknown units
 * (e.g. upload-row column names) scale by 1.
 */
const PLOT_SCALE: Record<string, number> = {
  scf: 1,
  mscf: 1000, // 1 MSCF = 1000 SCF
};

export function plotScale(unit: string | undefined): number {
  if (!unit) return 1;
  return PLOT_SCALE[unit] ?? 1;
}
